import { getApiUrl } from './api';

export interface SSEEvent {
  type: string;
  data: unknown;
}

export interface SSEConnection {
  disconnect: () => void;
}

export function createSSEConnection(
  onEvent: (event: SSEEvent) => void,
  onError: (error: Error) => void
): SSEConnection {
  const controller = new AbortController();
  const url = `${getApiUrl()}/sse/stream`;

  (async () => {
    try {
      const response = await fetch(url, {
        credentials: 'include',
        headers: { Accept: 'text/event-stream' },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE HTTP ${response.status}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let currentData = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith(':')) {
            continue;
          }
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          } else if (line === '') {
            if (currentData) {
              try {
                const parsed = JSON.parse(currentData);
                onEvent({ type: currentEvent || 'message', data: parsed });
              } catch {
                onEvent({ type: currentEvent || 'message', data: currentData });
              }
            }
            currentEvent = '';
            currentData = '';
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        onError(err);
      }
    }
  })();

  return {
    disconnect() {
      controller.abort();
    },
  };
}
